import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardList, Loader2, CheckCircle2, AlertTriangle, X,
  Eye, Download, Mail, CalendarPlus, Undo2,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import MarkAcceptedDialog from "@/components/treatment/MarkAcceptedDialog";
import SelectVisitProceduresDialog from "@/components/tx-plan-center/SelectVisitProceduresDialog";
import { buildTreatmentPlanPdf, pdfToBase64, pdfFilename } from "@/lib/treatmentPlanPdf";
import { sendTreatmentPlanEmail } from "@/lib/apiClient";
import { treatmentPlanDoctorGate } from "@/lib/treatmentPlanDoctorGate";
import { buildBookableVisitRows, resolveBookingDurationMinutes } from "@/lib/planVisits";
import { resolvePhaseCadenceForVisits } from "@/lib/txPlanPhaseCadence";

// =====================================================================
// OtherActivePlansFollowUp — Step 3 card surfacing the patient's OTHER
// active Tx Plans (Proposed, Accepted, or Needs Booking) that are NOT
// tied to today's appointment.
//
// Behavior:
//   - For each card: Book inline (no bounce to Doctor view) + PDF
//     actions (View / Download / Resend).
//   - Accepted plan: tap Book → SelectVisitProceduresDialog →
//     BookFutureNeedDialog → link every selected procedure_visit.
//   - Proposed plan: tap Accept → MarkAcceptedDialog → optional
//     SelectVisitProceduresDialog → BookFutureNeedDialog. This mirrors
//     Tx Plan Center so checkout never silently books just the first row.
//   - Skip with reason: writes treatment_plan_followup_log; never
//     blocks "Complete checkout" (soft prompt only).
// =====================================================================

const SKIP_REASONS = [
  { value: "budget",          label: "Budget" },
  { value: "needs_family",    label: "Needs family discussion" },
  { value: "not_today",       label: "Not today" },
  { value: "already_booked",  label: "Already booked elsewhere" },
  { value: "other",           label: "Other" },
];

function fmtMoney(cents, currency = "PHP") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format((Number(cents) || 0) / 100);
  } catch {
    return `₱${Math.round((Number(cents) || 0) / 100).toLocaleString()}`;
  }
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso);
    const now = new Date();
    const days = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    if (days < 1)  return "today";
    if (days < 2)  return "yesterday";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
  } catch {
    return "";
  }
}

function isAcceptedLikePlanStatus(status) {
  return ["accepted", "needs booking"].includes(String(status || "").trim().toLowerCase());
}

function isAcceptedScheduleResult(result) {
  if (result?.path !== "schedule") return false;
  return ["accepted", "needs booking", "scheduled", "in progress"]
    .includes(String(result?.status || "").trim().toLowerCase());
}

function firstVisitBookingHeading(status) {
  return String(status || "").trim().toLowerCase() === "proposed"
    ? "Accept plan & book first visit"
    : "Book first plan visit";
}

export default function OtherActivePlansFollowUp({
  appointmentId,
  patient,                  // full patient object (id, full_name, first_name, last_name, email, patient_number, etc.)
  currency = "PHP",
  defaultDoctorId,
  defaultChairId,
  priorVisitDate = null,
  onOpenQuotesChange = null,   // reports # of still-unresolved quotes to the parent (for the soft finish reminder)
  demoMode = false,
  demoPlans = null,
  demoPlanData = null,
  demoClinic = null,
  demoBookingData = null,
}) {
  const { activeClinic } = useAuth();
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [resolved, setResolved] = useState({});  // planId -> "skipped" | "booked" | "accepted" | "rejected"
  const [pickerFor, setPickerFor] = useState(null);
  const [pickerReason, setPickerReason] = useState("not_today");
  const [pickerNotes, setPickerNotes]   = useState("");
  const [submitting, setSubmitting]     = useState(false);

  // Clinic + plan-data cache for PDF + booking helpers.
  const [clinicForPdf, setClinicForPdf] = useState(null);
  const [pdfBusy, setPdfBusy]   = useState({});      // planId -> action label
  const [planDataCache, setPlanDataCache] = useState({}); // planId -> { plan, drawer }
  const [slot, setSlot] = useState(null);            // tx_plan_slot_state: {active_count, active_limit, ...}

  // Tx Plan Center booking flow state: acceptance review → visit-row
  // selection → slot picker → schedule_procedure_visit for every row.
  const [markAcceptedOpen, setMarkAcceptedOpen] = useState(false);
  const [planForAccept, setPlanForAccept] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [eligibleVisits, setEligibleVisits] = useState([]);
  const [pendingVisitIds, setPendingVisitIds] = useState([]);
  const [bookingFor, setBookingFor] = useState(null);   // planId
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [bookingPlanStatus, setBookingPlanStatus] = useState(null);
  const [bookingPriorVisitDate, setBookingPriorVisitDate] = useState(null);
  const [bookingCadenceDays, setBookingCadenceDays] = useState(null);

  const patientId = patient?.id;
  const patientName = patient?.full_name
                      || [patient?.first_name, patient?.last_name].filter(Boolean).join(" ").trim()
                      || "Patient";

  // ---- Initial load: other-active plans + clinic for PDF ---------------
  const load = useCallback(async () => {
    if (!appointmentId) return;
    if (demoMode) {
      setPlans(Array.isArray(demoPlans) ? demoPlans : []);
      setClinicForPdf(demoClinic || activeClinic || null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const [plansRes, clinicRes] = await Promise.all([
      supabase.rpc("checkout_other_active_plans", { p_appointment_id: appointmentId }),
      supabase.rpc("get_clinic_settings"),
    ]);
    if (plansRes.error) {
      setError(plansRes.error.message ?? "Could not load other active plans.");
      setLoading(false);
      return;
    }
    setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
    if (!clinicRes.error && clinicRes.data) {
      const s = clinicRes.data;
      const next = {
        ...s,
        clinic_id:       s.clinic_id,
        clinic_name:     s.name,
        name:            s.name,
        slug:            s.slug,
        timezone:        s.timezone,
        currency:        s.currency,
        address:         s.address     || null,
        phone:           s.phone       || null,
        email:           s.email       || null,
        prc_license:     s.prc_license || null,
        custom_plan_text: s.custom_plan_text || null,
        logo_data_url:   null,
      };
      if (s.logo_url) {
        try {
          const r = await fetch(s.logo_url);
          if (r.ok) {
            const blob = await r.blob();
            const dataUrl = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload  = () => resolve(fr.result);
              fr.onerror = () => reject(fr.error);
              fr.readAsDataURL(blob);
            });
            next.logo_data_url = dataUrl;
          }
        } catch { /* swallow */ }
      }
      setClinicForPdf(next);
    }
    setLoading(false);
  }, [activeClinic, appointmentId, demoClinic, demoMode, demoPlans]);

  useEffect(() => { load(); }, [load]);

  // ---- Plan-slot capacity (for the "up to 2 active" hint) --------------
  // A patient may hold at most `active_limit` (2) active plans. Accepting a
  // quote makes it active, so the front desk needs to know how many they can
  // still lock in today. Refetch when a quote is resolved (an acceptance
  // consumes a slot). Informational only — the DB cap is the real guard.
  useEffect(() => {
    if (demoMode || !patientId) { setSlot(null); return; }
    let alive = true;
    supabase.rpc("tx_plan_slot_state", { p_patient_id: patientId })
      .then(({ data, error }) => { if (alive && !error && data) setSlot(data); });
    return () => { alive = false; };
  }, [demoMode, patientId, resolved]);

  // ---- Plan + drawer lazy fetch (cached) -------------------------------
  const fetchPlanData = useCallback(async (planId, opts = {}) => {
    if (!opts.force && planDataCache[planId]) return planDataCache[planId];
    if (demoMode) {
      const entry = demoPlanData?.[planId] || null;
      if (entry) setPlanDataCache((prev) => ({ ...prev, [planId]: entry }));
      return entry;
    }
    const [planRes, drawerRes] = await Promise.all([
      supabase.rpc("get_treatment_plan", { p_plan_id: planId }),
      supabase.rpc("tx_plan_center_drawer", { p_plan_id: planId }),
    ]);
    if (planRes.error) {
      toast.error(planRes.error.message ?? "Could not load plan.");
      return null;
    }
    const entry = {
      plan:   planRes.data,
      drawer: drawerRes.error ? null : drawerRes.data,
    };
    setPlanDataCache((prev) => ({ ...prev, [planId]: entry }));
    return entry;
  }, [demoMode, demoPlanData, planDataCache]);

  const invalidatePlan = useCallback((planId) => {
    setPlanDataCache((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  }, []);

  const undoResolution = useCallback((planId) => {
    setResolved((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  }, []);

  // ---- PDF: View / Download / Resend ----------------------------------
  const buildPdfFor = useCallback(async (planId) => {
    const data = await fetchPlanData(planId);
    if (!data?.plan) return null;
    const doctorGate = treatmentPlanDoctorGate(data.plan);
    if (!doctorGate.ok) {
      toast.error(doctorGate.message);
      return null;
    }
    try {
      const doc = await buildTreatmentPlanPdf({
        plan:             data.plan,
        patient,
        clinic:           clinicForPdf || activeClinic,
        doctorName:       data.plan.doctor_name,
        doctorPrcLicense: data.plan.doctor_prc_license,
      });
      return { doc, planData: data.plan };
    } catch (err) {
      console.warn("[other-plans] PDF build failed", err);
      toast.error("Could not build the PDF.");
      return null;
    }
  }, [fetchPlanData, patient, clinicForPdf, activeClinic]);

  const onViewPdf = useCallback(async (planId) => {
    // Open the tab synchronously inside the click gesture. If we wait for the
    // async PDF build first, the browser no longer sees a user gesture and
    // blocks the pop-up. We fill this tab in once the PDF is ready.
    const win = window.open("", "_blank");
    setPdfBusy((b) => ({ ...b, [planId]: "view" }));
    const built = await buildPdfFor(planId);
    setPdfBusy((b) => ({ ...b, [planId]: null }));
    if (!built) { try { win?.close(); } catch { /* noop */ } return; }
    try {
      const blobUrl = built.doc.output("bloburl");
      if (win && !win.closed) {
        win.location.href = blobUrl;
      } else {
        // Pop-up was blocked — don't dead-end; hand over a download instead.
        built.doc.save(pdfFilename(patient));
        toast.message("Pop-up blocked — the plan PDF was downloaded instead.");
      }
    } catch (err) {
      console.warn("[other-plans] PDF preview failed, downloading instead", err);
      try { win?.close(); } catch { /* noop */ }
      built.doc.save(pdfFilename(patient));
      toast.message("Preview unavailable — the plan PDF was downloaded instead.");
    }
  }, [buildPdfFor, patient]);

  const onDownloadPdf = useCallback(async (planId) => {
    setPdfBusy((b) => ({ ...b, [planId]: "download" }));
    const built = await buildPdfFor(planId);
    setPdfBusy((b) => ({ ...b, [planId]: null }));
    if (!built) return;
    built.doc.save(pdfFilename(patient));
    toast.success("Plan PDF downloaded.");
  }, [buildPdfFor, patient]);

  const onResendEmail = useCallback(async (planId) => {
    if (!patient?.email) {
      // Fallback: download instead.
      await onDownloadPdf(planId);
      toast.warning("Patient has no email on file — PDF downloaded for manual sharing.");
      return;
    }
    setPdfBusy((b) => ({ ...b, [planId]: "email" }));
    const built = await buildPdfFor(planId);
    if (!built) {
      setPdfBusy((b) => ({ ...b, [planId]: null }));
      return;
    }
    let base64 = null;
    try { base64 = await pdfToBase64(built.doc); }
    catch { toast.error("Could not encode the PDF."); setPdfBusy((b) => ({ ...b, [planId]: null })); return; }

    const filename  = pdfFilename(patient);
    const fullName  = patientName;
    const firstName = patient?.first_name
                      || (patient?.full_name ? patient.full_name.split(/\s+/)[0] : "")
                      || "there";
    const planDate  = new Date().toLocaleDateString("en-PH",
      { year: "numeric", month: "long", day: "numeric" });
    const items = (built.planData?.items ?? []).filter((i) => i.status !== "Declined");
    const openTotal = items.reduce((s, i) => s + (i.fee_centavos ?? 0), 0);
    const payloadItems = items.map((it) => ({
      tooth_number:       it.tooth_number ? String(it.tooth_number) : null,
      procedure_name:     it.procedure_name || null,
      procedure_category: it.procedure_category || null,
      fee_centavos:       it.fee_centavos ?? 0,
      phase_number:       it.phase_number == null ? null : Number(it.phase_number),
    }));

    const res = await sendTreatmentPlanEmail({
      to_email:           patient.email,
      patient_first_name: firstName,
      patient_full_name:  fullName,
      clinic_name:        clinicForPdf?.name || activeClinic?.clinic_name || activeClinic?.name || "Dental Clinic",
      clinic_slug:        clinicForPdf?.slug || activeClinic?.slug || "",
      clinic_address:     clinicForPdf?.address     || null,
      clinic_phone:       clinicForPdf?.phone       || null,
      clinic_prc_license: built.planData?.doctor_prc_license || null,
      doctor_name:        built.planData?.doctor_name || null,
      plan_date:          planDate,
      items:              payloadItems,
      phase_names:        built.planData?.phase_names || {},
      phase_meta:         built.planData?.phase_meta  || {},
      tooth_notes:        built.planData?.tooth_notes || {},
      total_centavos:     openTotal,
      pdf_base64:         base64,
      pdf_filename:       filename,
    });
    setPdfBusy((b) => ({ ...b, [planId]: null }));
    if (res.ok) {
      toast.success("Plan resent to patient.");
    } else if (res.code === "domain_not_verified") {
      built.doc.save(filename);
      toast.warning("Email domain isn't verified yet — PDF downloaded so you can share it manually.");
    } else {
      built.doc.save(filename);
      toast.error(`${res.message ?? "Email failed."} PDF downloaded so you can send it manually.`);
    }
  }, [patient, patientName, clinicForPdf, activeClinic, buildPdfFor, onDownloadPdf]);

  const pendingVisits = useMemo(() => {
    const ids = new Set(pendingVisitIds);
    return eligibleVisits.filter((visit) => ids.has(visit.id));
  }, [eligibleVisits, pendingVisitIds]);

  const bookBundle = useMemo(() => {
    if (pendingVisits.length === 0) return null;
    const lead = pendingVisits[0];
    const categories = Array.from(new Set(
      pendingVisits.map((it) => it.procedure_category).filter(Boolean),
    ));
    const isMulti = pendingVisits.length > 1;
    const realMinutes = resolveBookingDurationMinutes(pendingVisits, planDataCache[bookingFor]?.plan?.phase_meta);
    const fallbackMinutes = 60 + Math.max(0, pendingVisits.length - 1) * 30;
    return {
      procedure_name:   isMulti ? `${pendingVisits.length} stages` : (lead.display_label || lead.procedure_name),
      tooth_number:     isMulti ? null : lead.tooth_number,
      surfaces:         isMulti ? [] : (lead.surface ? [lead.surface] : []),
      needs:            pendingVisits,
      estimatedMinutes: realMinutes > 0 ? realMinutes : fallbackMinutes,
      category:         lead.procedure_category || null,
      categories,
      isAll:            isMulti,
    };
  }, [pendingVisits, planDataCache, bookingFor]);

  // ---- Booking flow ---------------------------------------------------
  const primeBookingFlow = useCallback(async (planId, planStatus, opts = {}) => {
    setPdfBusy((b) => ({ ...b, [planId]: "loading" }));
    const data = await fetchPlanData(planId, { force: !!opts.force });
    setPdfBusy((b) => ({ ...b, [planId]: null }));
    if (!data) return;

    const visits = buildBookableVisitRows(data.plan?.items || []);
    if (visits.length === 0) {
      toast.error("No unbooked visits on this plan. Open the plan to review.");
      return;
    }

    let lastCompleted = null;
    for (const v of (data.drawer?.visits || [])) {
      if (!v?.completed_at) continue;
      if (!lastCompleted || new Date(v.completed_at) > new Date(lastCompleted)) {
        lastCompleted = v.completed_at;
      }
    }

    setBookingFor(planId);
    setBookingPlanStatus(planStatus);
    setEligibleVisits(visits);
    setPendingVisitIds([]);
    const initialCadence = visits.length === 1
      ? resolvePhaseCadenceForVisits({
          plan: data.plan,
          visits: data.drawer?.visits || [],
          selectedVisits: visits,
        })
      : null;
    setBookingPriorVisitDate(initialCadence?.anchorDate || lastCompleted || priorVisitDate || null);
    setBookingCadenceDays(initialCadence?.offsetDays ?? null);

    if (visits.length === 1) {
      setPendingVisitIds([visits[0].id]);
      setScheduleOpen(true);
    } else {
      setSelectOpen(true);
    }
  }, [fetchPlanData, priorVisitDate]);

  const openAcceptanceReview = useCallback(async (plan) => {
    if (!plan?.plan_id) return;
    if (demoMode) {
      setPlans((prev) => prev.map((p) => (
        p.plan_id === plan.plan_id
          ? { ...p, plan_status: "Accepted", accepted_at: p.accepted_at || new Date().toISOString() }
          : p
      )));
      await primeBookingFlow(plan.plan_id, "Proposed", { force: true });
      return;
    }
    setPdfBusy((b) => ({ ...b, [plan.plan_id]: "loading" }));
    const data = await fetchPlanData(plan.plan_id, { force: true });
    setPdfBusy((b) => ({ ...b, [plan.plan_id]: null }));
    if (!data?.plan) return;
    setPlanForAccept(data.plan);
    setBookingFor(plan.plan_id);
    setBookingPlanStatus("Proposed");
    setMarkAcceptedOpen(true);
  }, [demoMode, fetchPlanData, primeBookingFlow]);

  const startBooking = useCallback(async (plan) => {
    if (!plan?.plan_id) return;
    if (plan.plan_status === "Proposed") {
      await openAcceptanceReview(plan);
      return;
    }
    await primeBookingFlow(plan.plan_id, plan.plan_status);
  }, [openAcceptanceReview, primeBookingFlow]);

  const cancelBooking = useCallback(() => {
    setScheduleOpen(false);
    setSelectOpen(false);
    setPendingVisitIds([]);
    setEligibleVisits([]);
    setBookingFor(null);
    setBookingPlanStatus(null);
    setBookingPriorVisitDate(null);
    setBookingCadenceDays(null);
  }, []);

  const handleSelectionConfirmed = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    const selectedVisits = eligibleVisits.filter((visit) => ids.includes(visit.id));
    const data = bookingFor ? planDataCache[bookingFor] : null;
    const phaseCadence = data
      ? resolvePhaseCadenceForVisits({
          plan: data.plan,
          visits: data.drawer?.visits || [],
          selectedVisits,
        })
      : null;
    setPendingVisitIds(ids);
    if (phaseCadence?.anchorDate || phaseCadence?.offsetDays != null) {
      setBookingPriorVisitDate(phaseCadence?.anchorDate || bookingPriorVisitDate || priorVisitDate || null);
      setBookingCadenceDays(phaseCadence?.offsetDays ?? null);
    }
    setSelectOpen(false);
    setScheduleOpen(true);
  }, [bookingFor, bookingPriorVisitDate, eligibleVisits, planDataCache, priorVisitDate]);

  const onBooked = useCallback(async (apptId) => {
    if (!apptId || !bookingFor || pendingVisits.length === 0) {
      cancelBooking();
      return;
    }
    if (demoMode) {
      setResolved((prev) => ({ ...prev, [bookingFor]: "booked" }));
      toast.success(pendingVisits.length === 1 ? "Plan accepted and visit booked." : "Visit booked.");
      cancelBooking();
      return;
    }

    let okCount = 0;
    const failures = [];
    for (const target of pendingVisits) {
      const visitId = target.visit_id || target.procedure_visit_id || target.id;
      const { error: schedErr } = await supabase.rpc("schedule_procedure_visit", {
        p_visit_id:       visitId,
        p_appointment_id: apptId,
      });
      if (schedErr) {
        failures.push({ item: target, message: schedErr.message ?? "unknown error" });
      } else {
        okCount += 1;
      }
    }

    if (failures.length > 0) {
      toast.error(`${okCount} linked, ${failures.length} failed. First: ${failures[0].message}`);
      cancelBooking();
      return;
    }

    setResolved((prev) => ({ ...prev, [bookingFor]: "booked" }));
    invalidatePlan(bookingFor);
    toast.success(pendingVisits.length === 1
      ? (bookingPlanStatus === "Proposed" ? "Plan accepted and visit booked." : "Visit booked and linked.")
      : `Visit booked. ${okCount} procedures linked.`);
    cancelBooking();
  }, [bookingFor, bookingPlanStatus, cancelBooking, demoMode, invalidatePlan, pendingVisits]);

  const handleAccepted = useCallback(async (result) => {
    const planId = bookingFor || planForAccept?.id;
    setMarkAcceptedOpen(false);
    setPlanForAccept(null);
    if (!planId) return;

    const acceptedAt = new Date().toISOString();
    setPlans((prev) => prev.map((p) => (
      p.plan_id === planId
        ? {
            ...p,
            plan_status: result?.status || (result?.path === "reject" ? "Rejected" : "Accepted"),
            accepted_at: p.accepted_at || (result?.path === "reject" ? p.accepted_at : acceptedAt),
            total_fee_centavos: result?.plan_total_centavos ?? p.total_fee_centavos,
          }
        : p
    )));
    invalidatePlan(planId);

    // Any quotes closed by "Accept & close" leave the list entirely — they're
    // Superseded now, so they must not linger as stale cards (screen == truth).
    const supersededIds = Array.isArray(result?.superseded_plan_ids) ? result.superseded_plan_ids : [];
    if (supersededIds.length > 0) {
      setPlans((prev) => prev.filter((p) => !supersededIds.includes(p.plan_id)));
    }

    if (isAcceptedScheduleResult(result)) {
      await primeBookingFlow(planId, "Proposed", { force: true });
      return;
    }

    setResolved((prev) => ({
      ...prev,
      [planId]: result?.path === "reject" ? "rejected" : "accepted",
    }));
  }, [bookingFor, planForAccept, invalidatePlan, primeBookingFlow]);

  // ---- Skip with reason ------------------------------------------------
  const openPicker = useCallback((planId) => {
    setPickerFor(planId);
    setPickerReason("not_today");
    setPickerNotes("");
  }, []);

  const cancelPicker = useCallback(() => {
    setPickerFor(null);
    setPickerReason("not_today");
    setPickerNotes("");
  }, []);

  const submitSkip = useCallback(async () => {
    if (!pickerFor || submitting) return;
    if (demoMode) {
      setResolved((prev) => ({ ...prev, [pickerFor]: "skipped" }));
      toast.success("Plan marked as deferred. Recall Center will re-surface.");
      cancelPicker();
      return;
    }
    setSubmitting(true);
    const { error: e } = await supabase.rpc("log_plan_followup_skip", {
      p_appointment_id: appointmentId,
      p_plan_id:        pickerFor,
      p_reason:         pickerReason,
      p_notes:          pickerNotes.trim() || null,
    });
    setSubmitting(false);
    if (e) {
      toast.error(e.message ?? "Could not record skip.");
      return;
    }
    setResolved((prev) => ({ ...prev, [pickerFor]: "skipped" }));
    toast.success("Plan marked as deferred. Recall Center will re-surface.");
    cancelPicker();
  }, [appointmentId, cancelPicker, demoMode, pickerFor, pickerNotes, pickerReason, submitting]);

  // ---- Render ----------------------------------------------------------
  const visible = useMemo(
    () => plans.filter((p) => !resolved[p.plan_id]),
    [plans, resolved],
  );

  // Report the count of still-unresolved quotes up to the parent so it can
  // show a soft "you still have open quotes" reminder at Complete checkout.
  useEffect(() => {
    if (typeof onOpenQuotesChange === "function") onOpenQuotesChange(visible.length);
  }, [visible.length, onOpenQuotesChange]);

  // "Up to 2 active" capacity hint. proposedVisible = unresolved quotes that
  // would still need accepting (already-accepted plans only need booking).
  const proposedVisible = useMemo(
    () => visible.filter((p) => !isAcceptedLikePlanStatus(p.plan_status)).length,
    [visible],
  );
  const activeRemaining = slot
    ? Math.max(0, (Number(slot.active_limit) || 2) - (Number(slot.active_count) || 0))
    : null;
  const capacityHint = useMemo(() => {
    if (activeRemaining === null || proposedVisible === 0) return null;
    if (activeRemaining === 0) {
      return "This patient already has 2 active plans. Complete or book an existing one before accepting another.";
    }
    if (proposedVisible > activeRemaining) {
      return `You can accept up to ${activeRemaining} more plan${activeRemaining === 1 ? "" : "s"} today — max 2 active per patient.`;
    }
    return null;
  }, [activeRemaining, proposedVisible]);

  const skippedCount = useMemo(
    () => plans.filter((p) => resolved[p.plan_id] === "skipped").length,
    [plans, resolved],
  );
  const bookedCount = useMemo(
    () => plans.filter((p) => resolved[p.plan_id] === "booked").length,
    [plans, resolved],
  );
  const acceptedOnlyCount = useMemo(
    () => plans.filter((p) => resolved[p.plan_id] === "accepted").length,
    [plans, resolved],
  );
  const rejectedCount = useMemo(
    () => plans.filter((p) => resolved[p.plan_id] === "rejected").length,
    [plans, resolved],
  );
  const resolvedPlans = useMemo(
    () => plans
      .map((p) => ({ ...p, resolution: resolved[p.plan_id] }))
      .filter((p) => !!p.resolution),
    [plans, resolved],
  );

  if (!appointmentId || !patientId) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm px-5 py-4 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
        Checking patient's other active plans…
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
        {error}
      </div>
    );
  }

  if (visible.length === 0 && skippedCount === 0 && bookedCount === 0 && acceptedOnlyCount === 0 && rejectedCount === 0) return null;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-amber-200" data-testid="other-active-plans-card" data-tutorial-id="checkout-tx-plan-step3">
        <div className="px-5 pt-4 pb-2 flex items-start gap-2">
          <ClipboardList className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Patient has {visible.length || plans.length} other active plan{(visible.length || plans.length) === 1 ? "" : "s"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Best moment to book or defer — patient is at the desk right now.
            </p>
            {capacityHint ? (
              <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1" data-testid="other-plans-capacity-hint">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{capacityHint}</span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="px-5 pb-4 space-y-3">
          {visible.map((p) => {
            const isAccepted = isAcceptedLikePlanStatus(p.plan_status);
            const planLabel = `Plan · ${(p.plan_id || "").slice(0, 8)}`;
            const proposedAge   = fmtRelative(p.proposed_at);
            const acceptedAge   = fmtRelative(p.accepted_at) || fmtRelative(p.created_at);
            const lastSkipAge   = fmtRelative(p.last_skip_at);
            const lastSkipReason = SKIP_REASONS.find((r) => r.value === p.last_skip_reason)?.label;
            const unbooked = Number(p.unbooked_visit_count) || 0;
            const items    = Number(p.item_count) || 0;
            const total    = Number(p.total_fee_centavos) || 0;
            const isPickerOpen = pickerFor === p.plan_id;
            const busy = pdfBusy[p.plan_id];
            const primaryLabel = isAccepted
              ? "Book first visit"
              : "Accept plan & book first visit";

            return (
              <div key={p.plan_id}
                data-testid={`other-plan-card-${p.plan_id}`}
                data-tutorial-id={p.tutorial_id || undefined}
                className={`rounded-xl border ${
                  isAccepted
                    ? "border-teal-200 bg-teal-50/40"
                    : "border-amber-200 bg-amber-50/40"
                } px-4 py-3`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${
                        isAccepted
                          ? "bg-teal-600 text-white"
                          : "bg-amber-500 text-white"
                      }`}>
                        {p.plan_status}
                      </span>
                      <span className="font-mono text-xs text-slate-500">{planLabel}</span>
                      {isAccepted && unbooked > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-rose-100 text-rose-800">
                          <AlertTriangle className="w-3 h-3" />
                          {unbooked} unbooked visit{unbooked === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
                      <span className="text-base font-bold text-slate-900 tabular-nums">{fmtMoney(total, currency)}</span>
                      <span className="text-xs text-slate-500">
                        {items} item{items === 1 ? "" : "s"}
                        {isAccepted
                          ? ` · ${p.plan_status === "Needs Booking" ? "needs booking" : "accepted"} ${acceptedAge}`
                          : ` · proposed ${proposedAge}`}
                      </span>
                    </div>

                    {p.last_skip_at ? (
                      <p className="mt-1 text-[11px] text-slate-500">
                        Last skipped {lastSkipAge}
                        {lastSkipReason ? ` · ${lastSkipReason}` : ""}
                      </p>
                    ) : null}

                    {/* PDF action row */}
                    {!isPickerOpen ? (
                      <div className="mt-2 flex items-center gap-1 flex-wrap">
                        <button type="button" onClick={() => onViewPdf(p.plan_id)} disabled={!!busy}
                          data-testid={`other-plan-view-${p.plan_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-white rounded-md disabled:opacity-50">
                          {busy === "view" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                          View PDF
                        </button>
                        <button type="button" onClick={() => onDownloadPdf(p.plan_id)} disabled={!!busy}
                          data-testid={`other-plan-download-${p.plan_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-white rounded-md disabled:opacity-50">
                          {busy === "download" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                          Download
                        </button>
                        <button type="button" onClick={() => onResendEmail(p.plan_id)} disabled={!!busy}
                          data-testid={`other-plan-email-${p.plan_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-white rounded-md disabled:opacity-50"
                          title={patient?.email ? `Email to ${patient.email}` : "Patient has no email on file"}>
                          {busy === "email" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                          {patient?.email ? "Resend email" : "Resend (no email on file)"}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {!isPickerOpen ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => startBooking(p)} disabled={!!busy || submitting}
                        data-testid={`other-plan-book-${p.plan_id}`}
                        data-tutorial-id={p.book_tutorial_id || undefined}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
                        {busy === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                        {primaryLabel}
                      </button>
                      {!isAccepted ? (
                      <button type="button" onClick={() => openAcceptanceReview(p)} disabled={submitting || !!busy}
                        data-testid={`other-plan-accept-only-${p.plan_id}`}
                        data-tutorial-id={p.accept_only_tutorial_id || undefined}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-teal-800 bg-white hover:bg-teal-50 rounded-lg border border-teal-300 disabled:opacity-50">
                          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          Accept plan only
                        </button>
                      ) : null}
                      <button type="button" onClick={() => openPicker(p.plan_id)} disabled={submitting}
                        data-testid={`other-plan-skip-${p.plan_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-300 disabled:opacity-50">
                        Skip
                      </button>
                    </div>
                  ) : null}
                </div>

                {/* Inline skip-reason picker */}
                {isPickerOpen ? (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2"
                       data-testid={`other-plan-skip-picker-${p.plan_id}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        Why is the patient deferring?
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {SKIP_REASONS.map((r) => (
                        <button key={r.value} type="button"
                          onClick={() => setPickerReason(r.value)}
                          data-testid={`other-plan-skip-reason-${p.plan_id}-${r.value}`}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-md border ${
                            pickerReason === r.value
                              ? "bg-slate-800 border-slate-900 text-white"
                              : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                          }`}>
                          {r.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Optional note (e.g., 'wants to come back next month')"
                      value={pickerNotes}
                      maxLength={500}
                      onChange={(e) => setPickerNotes(e.target.value)}
                      data-testid={`other-plan-skip-notes-${p.plan_id}`}
                      className="w-full px-2.5 py-1.5 text-xs rounded-md border border-slate-300 focus:outline-none focus:border-teal-500"
                    />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={submitSkip} disabled={submitting}
                        data-testid={`other-plan-skip-confirm-${p.plan_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        Confirm skip
                      </button>
                      <button type="button" onClick={cancelPicker} disabled={submitting}
                        data-testid={`other-plan-skip-cancel-${p.plan_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700">
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {(skippedCount > 0 || bookedCount > 0 || acceptedOnlyCount > 0 || rejectedCount > 0) ? (
            <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap"
                 data-testid="other-plans-resolved-summary">
              {bookedCount > 0 ? (
                <span className="inline-flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {bookedCount} plan{bookedCount === 1 ? "" : "s"} booked
                </span>
              ) : null}
              {acceptedOnlyCount > 0 ? (
                <span className="inline-flex items-center gap-1.5 text-teal-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {acceptedOnlyCount} accepted without booking
                </span>
              ) : null}
              {skippedCount > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
                  {skippedCount} skipped — Recall will re-surface
                </span>
              ) : null}
              {rejectedCount > 0 ? (
                <span className="inline-flex items-center gap-1.5 text-rose-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {rejectedCount} rejected
                </span>
              ) : null}
              {resolvedPlans.map((p) => {
                const resolutionLabel = p.resolution === "booked"
                  ? "Booked first visit"
                  : p.resolution === "accepted"
                    ? "Accepted plan only"
                    : p.resolution === "rejected"
                      ? "Rejected"
                      : "Skipped";
                return (
                  <button key={p.plan_id} type="button"
                    onClick={() => undoResolution(p.plan_id)}
                    data-testid={`other-plan-undo-${p.plan_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    title={`${resolutionLabel}. Reopen this checkout choice; saved clinical records stay in place.`}>
                    <Undo2 className="w-3.5 h-3.5" />
                    Undo choice
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <MarkAcceptedDialog
        open={markAcceptedOpen}
        onOpenChange={(next) => {
          setMarkAcceptedOpen(next);
          if (!next) setPlanForAccept(null);
        }}
        plan={planForAccept}
        patientId={patientId}
        patientName={patient?.first_name || patientName}
        onAccepted={handleAccepted}
      />

      <SelectVisitProceduresDialog
        open={selectOpen}
        onOpenChange={(next) => {
          setSelectOpen(next);
          if (!next && !scheduleOpen) setPendingVisitIds([]);
        }}
        items={eligibleVisits}
        currency={currency}
        patientName={patientName}
        planCode={bookingFor ? `Plan · ${bookingFor.slice(0, 8)}` : null}
        onConfirm={handleSelectionConfirmed}
      />

      {/* Booking dialog — Scheduled status, NOT Pencil (not part of
          today's checkout commit batch). It uses the same selected
          procedure_visit rows as Tx Plan Center, so duration and
          linkage match the plan's real appointment structure. */}
      <BookFutureNeedDialog
        open={scheduleOpen}
        onOpenChange={(next) => {
          setScheduleOpen(next);
          if (!next) cancelBooking();
        }}
        bundle={bookBundle}
        patientId={patientId}
        patientName={patientName}
        defaultDoctorId={defaultDoctorId}
        defaultChairId={defaultChairId}
        appointmentStatus="Scheduled"
        priorVisitDate={bookingPriorVisitDate}
        doctorRequestedDays={bookingCadenceDays}
        headingOverride={isAcceptedLikePlanStatus(bookingPlanStatus) || bookingPlanStatus === "Proposed"
          ? firstVisitBookingHeading(bookingPlanStatus)
          : "Book next plan visit"}
        subtitleOverride={pendingVisits.length === 1 ? pendingVisits[0]?.display_label || pendingVisits[0]?.procedure_name : null}
        hideHintForVisitOne={!bookingPriorVisitDate}
        demoMode={demoMode}
        demoInitialDate={demoMode ? "2026-05-19" : undefined}
        demoReferenceData={demoBookingData || undefined}
        demoDayAppointments={demoMode ? [] : undefined}
        demoUpcomingAppointments={demoMode ? [] : undefined}
        onBooked={onBooked}
      />
    </>
  );
}
